import { get } from 'lodash-es'
import { FLOW_STEPS } from '@/constants/flow-steps'
import { ENROLLMENT_TOUCHPOINTS } from '@/constants/personalization-studio'
import { FLOWS, FLOWS_SUPPORTED_PRODUCTS } from 'universal/ucs'
import { EVALUATE_FUNDING_OPTIONS_ENTRY_POINTS } from 'universal/entry-points'
import {
  getProductSystemNameByAlias,
  APPLICATION_STATUS,
  NEED_MORE_INFO_VALUES,
  PRODUCT_NAMES,
} from 'universal/cfs-card'
import { ADDRESS_TYPES } from 'universal/address'
import {
  getCardManagementPath,
  generateCipRedirectUrl,
  PP_SUMMARY,
  generateKYCRedirectURL,
} from 'universal/routes'
import { isCIPVerified, isCIPAborted, isCIPFailure } from 'universal/cip'
import { ISSUANCE_ELIGIBILITY_REASONS } from 'universal/issuance'
import { INTENT_TYPES } from 'universal/constants'
import { isValidProductName } from 'utils/utils'
import { getQueryParam, getQueryParams } from 'utils/query'
import { hasFeature } from 'utils/server-data'
import { constructStepUpRedirectUrl, constructStepUpRedirectUrlWithOptions } from 'utils/risk'
import { redirectTo, goTo, goToChangeAddressFlow, ppdcXOredirectTo } from 'utils/navigate'
import { createAddAddressFlowUrl } from 'utils/flow'
import { handleUnexpectedError } from 'utils/error/error-handler'
import { errorCodes } from 'universal/error-codes'

const { ENROLL_CARD } = FLOWS
const { ADD_FI, CANCEL_ADD_FI } = EVALUATE_FUNDING_OPTIONS_ENTRY_POINTS
const { NEED_MORE_DATA_STEP, MANUAL_REVIEW_STEP, DENIED_STEP, CARD_ENROLLING_STEP } =
  FLOW_STEPS.ENROLL_CARD
const { APPROVED, STEPUP_AUTH, NEED_MORE_DATA, MANUAL_REVIEW, DENIED } = APPLICATION_STATUS
const { DENIED_BY_RISK } = errorCodes
const { SIMILAR_PRODUCT_EXISTS, ACCOUNT_TYPE_INVALID } = ISSUANCE_ELIGIBILITY_REASONS
const { BUSINESS_DEBIT_CARD, CONSUMER_DEBIT_CARD } = PRODUCT_NAMES

const isCIPAbortedOrFailureForEnrollment = () => {
  const status = getQueryParam('status') || ''
  return isCIPAborted(status) || isCIPFailure(status)
}

export const isShippingAddressRequired = (applyCard: any = {}) => {
  const { errorStatus, requiredValue } = applyCard
  return errorStatus === NEED_MORE_DATA && requiredValue === NEED_MORE_INFO_VALUES.SHIPPING_ADDRESS
}

const isCfpbRequired = (application: any = {}) => {
  return application?.requiredValue === NEED_MORE_INFO_VALUES.CFPB_LEGAL_LOGGING
}

const isCipRequired = (application: any = {}) => {
  return application?.requiredValue === NEED_MORE_INFO_VALUES.SSN
}

export const shouldPreventCipRedirection = (application: any = {}) => {
  return isCipRequired(application) && isCIPAbortedOrFailureForEnrollment()
}

export const isCIPVerifiedForEnrollment = () => {
  const { status = '', statusCode = '' } = getQueryParams() as any
  return isCIPVerified(status.toUpperCase(), statusCode.toUpperCase())
}

export function checkRUGSResponse({ applyCard, nextStep, dispatch }) {
  const { errorStatus, stepUpContext } = applyCard

  switch (errorStatus) {
    case STEPUP_AUTH:
      const stepUrlQueryParams = getQueryParams()

      const stepUpUrl = constructStepUpRedirectUrl(stepUpContext, ENROLL_CARD, {
        ...stepUrlQueryParams,
      })

      redirectTo(stepUpUrl, { dispatch })
      return true

    case NEED_MORE_DATA:
      nextStep(NEED_MORE_DATA_STEP)
      return true

    case MANUAL_REVIEW:
      nextStep(MANUAL_REVIEW_STEP)
      return true

    case DENIED:
      nextStep(DENIED_STEP)
      return true

    default:
      throw new Error(`Unhandled apply card RUGS status ${errorStatus}`)
  }
}

export function checkRUGSResponseGB({ applyCard, _nextStep, goToNextStep, dispatch, selectedFundingInstrumentId }) {
  const { errorStatus, stepUpContext } = applyCard

  switch (errorStatus) {
    case STEPUP_AUTH:
      const stepUrlQueryParams = getQueryParams()

      const stepUpUrl = constructStepUpRedirectUrlWithOptions(
        stepUpContext,
        ENROLL_CARD,
        {
          ...stepUrlQueryParams,
        },
        { page: CARD_ENROLLING_STEP, selectedFundingInstrumentId },
      )

      redirectTo(stepUpUrl, { dispatch })
      return true

    case NEED_MORE_DATA:
      goToNextStep('lastStep', NEED_MORE_DATA_STEP)
      return true

    case MANUAL_REVIEW:
      goToNextStep('lastStep', MANUAL_REVIEW_STEP)
      return true

    case DENIED:
      goToNextStep('lastStep', DENIED_STEP)
      return true

    default:
      throw new Error(`Unhandled apply card RUGS status ${errorStatus}`)
  }
}

export function checkRUGSResponsePPDCXO({ applyCard, dispatch, options =  {}}) {
  const { errorStatus, stepUpContext } = applyCard

  switch (errorStatus) {
    case STEPUP_AUTH:
      const stepUrlQueryParams = getQueryParams()

      const stepUpUrl = constructStepUpRedirectUrlWithOptions(
        stepUpContext,
        ENROLL_CARD,
        {
          ...stepUrlQueryParams,
        },
        options,
      )

      ppdcXOredirectTo(stepUpUrl, { dispatch })
      
      return true

    default:
      throw new Error(`Unhandled apply card RUGS status ${errorStatus}`)
  }
}

export function handleReissueCardError(error, options: any = {}) {
  const { goToNextStep, dispatch } = options

  try {
    switch (error.code) {
      case errorCodes.STEPUP_AUTH:
        const { stepUpContext } = error
        const stepUpUrl = constructStepUpRedirectUrl(stepUpContext, ENROLL_CARD, {
          ...getQueryParams(),
        })
        redirectTo(stepUpUrl, { dispatch })
        break

      case DENIED_BY_RISK:
        goToNextStep('lastStep', DENIED_STEP)
        break

      default:
        handleUnexpectedError(error, options)
    }
  } catch (err) {
    throw err
  }
}

export const overrideEnrollCardFlowFirstStepByEligibility = ({
  cardApplication,
  cardEligibility = {},
  otherCardEligibilities = {},
}: any) => {
  const { eligible = false } = cardEligibility || {}
  const isCardApplicationApproved = cardApplication?.status === APPROVED
  const page = getQueryParam('page')

  if (isCardApplicationApproved && page) {
    switch (page) {
      case CARD_ENROLLING_STEP:
        return {
          firstStep: 4,
          hideStepsBeforeFirst: true,
        }
      default:
    }
  }

  if (eligible) {
    return {
      firstStep: 2,
      hideStepsBeforeFirst: true,
    }
  }

  const isEligibleForBDMC = otherCardEligibilities?.[BUSINESS_DEBIT_CARD]?.eligible

  if (isEligibleForBDMC) {
    return {
      firstStep: 0,
      hideStepsBeforeFirst: true,
    }
  }

  return {
    firstStep: 1,
    hideStepsBeforeFirst: true,
  }
}

export const overrideEnrollCardFlowFirstStepByEligibilityWithPreEligibility = ({
  cardEligibility = {},
}: any) => {
  const { eligibilityStatus = 'INELIGIBLE', details = [] } = cardEligibility || {}
  const page = getQueryParam('page')
  const cardId = getQueryParam('cardId')

  const isPostProvisioningReturn =
    cardId &&
    (page === 'cardEnrolling' || page === 'cardSuccess') &&
    eligibilityStatus === 'INELIGIBLE' &&
    details.some((detail) => detail.reason === SIMILAR_PRODUCT_EXISTS)

  if (isPostProvisioningReturn) {
    return {
      firstStep: 6, // CARD_REISSUING_STEP
      hideStepsBeforeFirst: true,
    }
  }

  // if (isCardApplicationApproved && page) {
  //   switch (page) {
  //     case CARD_ENROLLING_STEP:
  //       return {
  //         firstStep: 4,
  //         hideStepsBeforeFirst: true,
  //       }
  //     default:
  //   }
  // }

  if (eligibilityStatus === 'ELIGIBLE_WITH_CONDITION' || eligibilityStatus === 'ELIGIBLE') {
    return {
      firstStep: 2,
      hideStepsBeforeFirst: true,
    }
  }

  // const isEligibleForBDMC = otherCardEligibilities?.[BUSINESS_DEBIT_CARD]?.eligible

  // if (isEligibleForBDMC) {
  //   return {
  //     firstStep: 0,
  //     hideStepsBeforeFirst: true,
  //   }
  // }

  return {
    firstStep: 1,
    hideStepsBeforeFirst: true,
  }
}

export function overrideEnrollCardFlowFirstStepByErrorStatus(
  cardApplication,
  dispatch,
  productName,
) {
  const { errorStatus, stepUpContext } = cardApplication || {}
  const defaultErrorStepValues = {
    firstStep: 3,
    hideStepsBeforeFirst: true,
  }

  switch (errorStatus) {
    case STEPUP_AUTH:
      const { failedBecause: failedStepUp, stepUrlQueryParams } = getQueryParams() as any
      if (failedStepUp) {
        return {}
      }

      const stepUpUrl = constructStepUpRedirectUrl(stepUpContext, ENROLL_CARD, {
        ...stepUrlQueryParams,
      })

      redirectTo(stepUpUrl, { dispatch })
      return {}

    case NEED_MORE_DATA:
      const hasShippingAddressRequirement = isShippingAddressRequired(cardApplication)

      if (isCIPVerifiedForEnrollment() && hasShippingAddressRequirement) {
        ;(goToChangeAddressFlow as any)({
          flowName: ENROLL_CARD,
          productName,
          addressType: ADDRESS_TYPES.SHIPPING,
        })
        return {}
      }

      const hasCFPBorShippingAddressRequirement =
        isCfpbRequired(cardApplication) || hasShippingAddressRequirement

      if (hasCFPBorShippingAddressRequirement || shouldPreventCipRedirection(cardApplication)) {
        return {
          firstStep: 2,
          hideStepsBeforeFirst: true,
        }
      }

      return {
        firstStepSwitchName: NEED_MORE_DATA_STEP,
        ...defaultErrorStepValues,
      }

    case MANUAL_REVIEW:
      return {
        firstStepSwitchName: MANUAL_REVIEW_STEP,
        ...defaultErrorStepValues,
      }

    case DENIED:
      return {
        firstStepSwitchName: DENIED_STEP,
        ...defaultErrorStepValues,
      }

    default:
      return {
        firstStep: 1,
        hideStepsBeforeFirst: true,
      }
  }
}

export function overrideEnrollCardFlowFirstStepByQueryParams(firstStepInfo, cardEligibility) {
  const { KYC_Redirect, entrypoint, page, rs } = getQueryParams() as any

  // Check for KYC redirection first, as it should take priority
  if (KYC_Redirect === 'true') {
    const { eligibilityStatus, details } = cardEligibility || {}
    if (eligibilityStatus === 'ELIGIBLE') {
      return {
        firstStep: 5, // CARD_SUCCESS_STEP
        hideStepsBeforeFirst: true,
      }
    }
    if (eligibilityStatus === 'ELIGIBLE_WITH_CONDITION') {
      const reason = details?.[0]?.reason
      if (reason === 'MANUAL_REVIEW') {
        return {
          firstStep: 4, // CARD_ENROLLING_STEP
          firstStepSwitchName: MANUAL_REVIEW_STEP,
          hideStepsBeforeFirst: true,
        }
      }
    }
    if (eligibilityStatus === 'INELIGIBLE') {
      return {
        firstStep: 4, // CARD_ENROLLING_STEP
        firstStepSwitchName: DENIED_STEP,
        hideStepsBeforeFirst: true,
      }
    }
  }

  // Check for auth flow return (rs=1 with page=cardEnrolling)
  if (rs === '1' && page === 'cardEnrolling') {
    return {
      firstStep: 5, // CARD_ENROLLING_STEP (step 4 is StepSwitch)
      hideStepsBeforeFirst: true,
    }
  }

  // Only check entrypoint if not coming from KYC
  if (entrypoint === ADD_FI || entrypoint === CANCEL_ADD_FI) {
    return {
      firstStep: 3, // FUNDING_INSTRUMENT_STEP
      hideStepsBeforeFirst: true,
    }
  }
  return firstStepInfo
}


export function checkKYCandRedirect(
  cardEligibility: any = {},
  dispatch,
  nextStep,
  goToNextStep,
  selectedFundingInstrumentId,
) {
  const { eligibilityStatus, details } = cardEligibility || {}

  if (eligibilityStatus === 'ELIGIBLE') {
    goToNextStep(CARD_ENROLLING_STEP)
    return
  }

  if (eligibilityStatus === 'ELIGIBLE_WITH_CONDITION') {
    const reason = details?.[0]?.reason
    if (reason === 'MANUAL_REVIEW') {
      nextStep(MANUAL_REVIEW_STEP)
      return
    } else if (
      reason === 'IDENTITY_VERIFICATION_REQUIRED' ||
      reason === 'IDENTITY_VERIFICATION_FAILED'
    ) {
      const policyDashboardUrl = generateKYCRedirectURL(selectedFundingInstrumentId)
      redirectTo(policyDashboardUrl, { dispatch })
      return
    }
  }

  if (eligibilityStatus === 'INELIGIBLE') {
    nextStep(DENIED_STEP)
    return
  }

  const policyDashboardUrl = generateKYCRedirectURL(selectedFundingInstrumentId)
  redirectTo(policyDashboardUrl, { dispatch })
  return
}

export function enrollCardFlowEligibilityCheck() {
  const productName = getQueryParam('productName')

  if (
    !productName ||
    !hasFeature('enableEnrollCard') ||
    !FLOWS_SUPPORTED_PRODUCTS[ENROLL_CARD].includes(productName)
  ) {
    return false
  }

  return true
}

export const goToAddAddressFlow = ({ productName, addressType = ADDRESS_TYPES.SHIPPING }) => {
  if (!productName) return

  const returnUri = getCardManagementPath({ productName })
  const cancelUri = getQueryParam('returnUri') || PP_SUMMARY
  const addNewAddressFlowUrl = createAddAddressFlowUrl(
    { productName, addressType },
    returnUri,
    cancelUri,
  )

  if (!addNewAddressFlowUrl) {
    return
  }

  goTo(addNewAddressFlowUrl)
}

export const filterRecommendations = (recommendations = []) => {
  return recommendations.reduce((filteredRecommendations, recommendation) => {
    const touchPointCode = recommendation.touchpointCode
    const messages = recommendation.messages
    if (touchPointCode && messages) {
      filteredRecommendations[touchPointCode] = filterMessages(messages)
    }

    return filteredRecommendations
  }, {})

  function filterMessages(messages) {
    return messages.map((message) => {
      const content = get(message, 'content.json', {})

      for (const [key, value] of Object.entries(content)) {
        try {
          if(key === 'offerUnitId') {
            content[key] = value // keep as string
            continue
          }
          content[key] = JSON.parse(value as string)
        } catch {
          // not json parsable
        }
      }

      return {
        content,
      }
    })
  }
}

export const arrangeRecommendations = (recommendations, step = '') => {
  const arrangedRecommendations = []

  ENROLLMENT_TOUCHPOINTS[step].forEach((touchpoint) =>
    arrangedRecommendations.push(...(recommendations[touchpoint] || [])),
  )

  return arrangedRecommendations
}

export function getFlowInputParams() {
  const { productName, directEntry, skipSuccess } = getQueryParams() as any

  return {
    productName: isValidProductName(ENROLL_CARD, productName)
      ? getProductSystemNameByAlias(productName)
      : null,
    directEntry: Boolean(directEntry),
    addressId: getQueryParam('addressId'),
    skipSuccess: skipSuccess === 'true',
  }
}

function hasNeedMoreDataErrorStatus(applyCard: any = {}) {
  return applyCard?.errorStatus === NEED_MORE_DATA
}

function hasManualReviewErrorStatus(applyCard: any = {}) {
  return applyCard?.errorStatus === MANUAL_REVIEW
}

function isSimilarProductExist(details) {
  if (Array.isArray(details)) {
    const foundSimilarProduct = details.find((detail) => detail?.reason === SIMILAR_PRODUCT_EXISTS)
    return foundSimilarProduct
  }
  return false
}

function isInvalidAccountType(details) {
  return Array.isArray(details) && details.find((detail) => detail?.reason === ACCOUNT_TYPE_INVALID)
}

export function shouldRedirectToCardManagement(cardEligibilities = {}) {
  const similarCardProducts = checkForSimilarCardProducts(cardEligibilities)
  return similarCardProducts[CONSUMER_DEBIT_CARD]
}

function overwriteReturnAndSuccessUrlInCfpbUrl(pathName = '') {
  if (!pathName) {
    return ''
  }

  const currentUrl = window.location.pathname + window.location.search
  const url = new URL(`${window.location.origin}${pathName}`)
  url.searchParams.set('returnUrl', currentUrl)
  url.searchParams.set('successUrl', currentUrl)

  return `${url.pathname}?${url.searchParams}`
}

export function shouldRedirectToCfpbPage(applyCard: any = {}, dispatch) {
  if (hasNeedMoreDataErrorStatus(applyCard) && applyCard?.cfpbRedirectUrl) {
    const updatedCfpbRedirectUrl = overwriteReturnAndSuccessUrlInCfpbUrl(applyCard.cfpbRedirectUrl)
    redirectTo(updatedCfpbRedirectUrl, { dispatch })
    return true
  }
  return false
}

export function shouldRedirectToCipPage(applyCard: any = {}, dispatch) {
  if (
    !shouldPreventCipRedirection(applyCard) &&
    (hasNeedMoreDataErrorStatus(applyCard) || hasManualReviewErrorStatus(applyCard)) &&
    isCipRequired(applyCard)
  ) {
    const cipRedirectUrl = generateCipRedirectUrl({ flow: ENROLL_CARD })
    redirectTo(cipRedirectUrl, { dispatch })
    return true
  }
  return false
}

export const isFromEnrollmentFlow = (flowName) => {
  return flowName === FLOWS.ENROLL_CARD
}

export const hasPayrollPartnerIntent = () => {
  const intent = getQueryParam('intent')
  return intent === INTENT_TYPES.PAYROLL_PARTNER
}

export const getFieldsToIncludeByFlow = () => {
  let includeFields = {}

  if (hasPayrollPartnerIntent()) {
    includeFields = {
      userCards: 'userCards',
    }
  }

  return includeFields
}

function checkForSimilarCardProducts(cardEligibilities) {
  const ppdcEligibilityDetails = get(
    cardEligibilities,
    `${CONSUMER_DEBIT_CARD}.resultDetail.details`,
    [],
  )
  const bdmcEligibilityDetails = get(
    cardEligibilities,
    `${BUSINESS_DEBIT_CARD}.resultDetail.details`,
    [],
  )

  return {
    [CONSUMER_DEBIT_CARD]:
      isSimilarProductExist(ppdcEligibilityDetails) &&
      !isInvalidAccountType(ppdcEligibilityDetails),
    [BUSINESS_DEBIT_CARD]:
      isSimilarProductExist(bdmcEligibilityDetails) &&
      !isInvalidAccountType(bdmcEligibilityDetails),
  }
}
